import { Controller } from "@hotwired/stimulus";
import { get } from "@rails/request.js";

const waitingHtml =
  "<div class='spinner'><div class='double-bounce1'></div><div class='double-bounce2'></div></div>";

export default class extends Controller {
  static values = { autoOpen: Boolean };

  connect() {
    this._onModalClose = this.closeModal.bind(this);
    this._onModalReset = (event) => this.resetModal(event.target);
    document.addEventListener("modalClose", this._onModalClose);
    document.addEventListener("modalReset", this._onModalReset);
    this.scrollPosition = 0;

    // Only the dimmer (singleton per layout) registers the global escape
    // handler — same anchor as closeModalFromDimmer, so we don't fire one
    // close per controller instance on the page.
    if (this.element.id === "dimmer") {
      this._onKeydown = (event) => this._handleKeydown(event);
      document.addEventListener("keydown", this._onKeydown);
    }

    if (this.autoOpenValue) {
      this._openModal(this.element.id);
    }
  }

  disconnect() {
    if (this._onModalClose) {
      document.removeEventListener("modalClose", this._onModalClose);
    }
    if (this._onModalReset) {
      document.removeEventListener("modalReset", this._onModalReset);
    }
    if (this._onKeydown) {
      document.removeEventListener("keydown", this._onKeydown);
    }
  }

  _handleKeydown(event) {
    if (event.key !== "Escape") return;
    const modal = this.currentModal;
    if (!modal || modal.classList.contains("locked")) return;
    this.closeModal();
    this.resetHistory();
  }

  openModal(event) {
    event.preventDefault();
    const id = event.currentTarget.dataset.modalId;
    const url = event.currentTarget.dataset.modalUrl;
    this._openModal(id);
    if (url) {
      get(url, { responseKind: "turbo-stream" });
    }
  }

  openUp(event) {
    const id = event.target.id;
    this._openModal(id);
  }

  // Open Modals
  _openModal(themodalId) {
    // Capture current scroll position
    this.scrollPosition = window.scrollY;

    const themodal = document.getElementById(themodalId);
    themodal.style.display = "inline";
    themodal.classList.add("is-open");
    themodal.querySelectorAll(".modal-content").forEach((el) => (el.scrollTop = 0));
    const waiting = themodal.getElementsByClassName("waiting");
    if (waiting.length) {
      waiting[0].innerHTML = waitingHtml;
    }
    this.dimmer.style.display = "inline";

    // Prevent body scroll and maintain position
    document.body.classList.add("is-fixed");
    document.body.style.top = `-${this.scrollPosition}px`;

    const event = new CustomEvent("modalOpen", {
      bubbles: true,
      cancelable: true,
    });
    themodal.dispatchEvent(event);
  }

  goBack() {
    this.closeModal();
    this.fireGoBackward();
  }

  goForward(next) {
    this.setHistory();
    const modalToReset = this.currentModal;
    this.closeModal();
    this.resetModal(modalToReset);
    const next_modal_identifier = typeof next === "string" ? next : this.data.get("next");
    if (next_modal_identifier) {
      this._openModal(next_modal_identifier);
    }
  }

  closeModalFromDimmer() {
    if (!this.currentModal) return;
    if (this.currentModal.classList.contains("locked")) {
      return;
    } else {
      this.closeModal();
      this.resetHistory();
    }
  }

  // Close Modals
  closeModal() {
    this.dimmer.style.display = "none";

    // Restore body scroll
    document.body.classList.remove("is-fixed");
    document.body.style.top = "";

    // Restore scroll position
    window.scrollTo(0, this.scrollPosition);

    if (this.currentModal) {
      this.currentModal.querySelectorAll("audio").forEach((audio) => audio.pause());
      this.currentModal.style.display = "none";
      this.currentModal.classList.remove("is-open");
    }
  }

  // Reset all form inputs within a modal to their default state
  resetModal(modal) {
    if (!modal) return;

    modal.querySelectorAll("form").forEach((form) => form.reset());
    modal.querySelectorAll("textarea").forEach((el) => (el.value = ""));
    modal.querySelectorAll(".selection-label").forEach((el) => el.remove());

    // Re-disable submit buttons managed by selectlist
    modal.querySelectorAll("[data-selectlist-target='submit']").forEach((btn) => {
      btn.classList.add("disabled");
      btn.disabled = true;
    });
  }

  setHistory() {
    this.dispatchCustomEvent("setHistory");
  }

  fireGoBackward() {
    this.dispatchCustomEvent("goBackward");
  }

  resetHistory() {
    this.dispatchCustomEvent("resetHistory");
  }

  dispatchCustomEvent(eventName) {
    const event = new CustomEvent(eventName, {
      bubbles: true,
      cancelable: true,
    });
    this.element.dispatchEvent(event);
  }

  get dimmer() {
    return document.getElementById("dimmer");
  }

  get currentModal() {
    return document.querySelector(".modal.is-open");
  }
}
